import React, { useState, useEffect, useRef } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import { resolveAssetUrl } from "../../api/config";
import Footer from "../../components/footer";
import Swal from "sweetalert2";
import {
  FaBalanceScale,
  FaBuilding,
  FaCalendarAlt,
  FaEnvelope,
  FaExclamationTriangle,
  FaGraduationCap,
  FaHandshake,
  FaMapMarkerAlt,
  FaMedal,
  FaPhone,
  FaPlus,
  FaRunning,
  FaSearch,
  FaTimes,
  FaTrash,
  FaTrophy
} from "react-icons/fa";

const normalizeMobile = (value) => {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.substring(1);
  } else if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.substring(2);
  }
  return digits.substring(0, 10);
};

const getMainDocLabel = (type) => {
  const labels = {
    player: "Merit Certificates",
    association: "Player Details & Certificates",
    district_association: "Player Details & Certificates",
    coach: "Athletes Trained Details & Certificates",
    technical_official: "Technical Official Certification & Event Details",
    classifier: "Classifier Certification & Event Details",
    support_staff: "Experience & Support Staff Proof",
    ngo: "Registration & Supported Players Achievements",
    educational_institution: "Student Athlete Lists & Sports Facility Proof",
    individual_organization: "Individual Contribution Letter & Event Files",
  };
  return labels[type] || "Evidence / Registration Document";
};

const getMainDocDesc = (type) => {
  const descs = {
    player: "Upload all merit certificates achieved till now.",
    association: "Upload your player details list and their corresponding sports certificates.",
    district_association: "Upload your player details list and their corresponding sports certificates.",
    coach: "Upload athlete lists, event files, and certificates of players training under you.",
    technical_official: "Upload technical official certification and officiating event details.",
    classifier: "Upload classifier certification and event details.",
    support_staff: "Upload experience certificates, supporting event proofs, and photos.",
    ngo: "Upload NGO registration documents, list of supported athletes, and their achievements.",
    educational_institution: "Upload student athlete lists and proof of sports infrastructure.",
    individual_organization: "Upload individual contribution letters, recommendations, and event files.",
  };
  return descs[type] || "Upload your primary registration document or main achievements file.";
};

const AWARD_CATEGORIES = [
  { id: 1, name: "Best Para Coach" },
  { id: 2, name: "Best Junior Para Sportsman" },
  { id: 3, name: "Best Junior Para Sportswoman" },
  { id: 4, name: "Best Sub-Junior Para Sportsman" },
  { id: 5, name: "Best Sub-Junior Para Sportswoman" },
  { id: 6, name: "Best Senior Para Sportsman" },
  { id: 7, name: "Best Senior Para Sportswoman" },
  { id: 8, name: "Best International Para Athlete" },
  { id: 9, name: "Best National Para Athlete" },
  { id: 10, name: "Best Emerging Para Athlete" },
  { id: 11, name: "Best Para Team" },
  { id: 12, name: "Best District Para Sports Association" },
  { id: 13, name: "Best Para Sports NGO / Organization" },
  { id: 14, name: "Best Technical Official / Referee" },
  { id: 15, name: "Best Classifier" },
  { id: 16, name: "Best Support Staff (Physiotherapist, Trainer, Volunteer, Team Manager, etc.)" },
  { id: 17, name: "Lifetime Achievement Award for Outstanding Contribution to Para Sports" },
  { id: 18, name: "Special Jury Award for Exceptional Service to Para Sports" },
  { id: 19, name: "Best Para Escort" },
  { id: 20, name: "Best Para Guide Runner" }
];

const CATEGORIES_BY_TYPE = {
  player: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 17, 18, 20],
  coach: [1, 17, 18],
  technical_official: [14, 18],
  classifier: [15, 18],
  support_staff: [16, 18, 19, 20],
  association: [12, 18],
  ngo: [13, 18],
  educational_institution: [18],
  individual_organization: [17, 18]
};

const APPLICANT_TYPES = [
  { id: "player", label: "Para Athlete (Sub-Junior, Junior, Senior)" },
  { id: "coach", label: "Coach" },
  { id: "technical_official", label: "Technical Official / Referee" },
  { id: "classifier", label: "Classifier" },
  { id: "support_staff", label: "Support Staff (Physio, Trainer, Volunteer, Manager)" },
  { id: "association", label: "District Para Sports Association" },
  { id: "ngo", label: "NGO / Sports Organization" },
  { id: "educational_institution", label: "Educational Institution" },
  { id: "individual_organization", label: "Individual or Other Organization" }
];

const AP_DISTRICTS = [
  "Alluri Sitharama Raju",
  "Anakapalli",
  "Ananthapur",
  "Annamayya",
  "Bapatla",
  "Chittoor",
  "Dr. B. R. Ambedkar Konaseema",
  "East Godavari",
  "Eluru",
  "Guntur",
  "Kakinada",
  "Krishna",
  "Kurnool",
  "Markapuram",
  "Nandyal",
  "NTR",
  "Palnadu",
  "Parvathipuram Manyam",
  "Polavaram",
  "Prakasam",
  "Sri Potti Sriramulu Nellore",
  "Sri Sathya Sai",
  "Srikakulam",
  "Tirupati",
  "Visakhapatnam",
  "Vizianagaram",
  "West Godavari",
  "YSR Kadapa"
];

const CATEGORY_NOTES = {
  player: "Please upload corresponding medal certificates and age proof (DOB proof).",
  coach: "Please upload player details, their achievements, and supporting documents.",
  technical_official: "Please upload officiating certificates, seminar details, and contributions to para sports.",
  classifier: "Please upload classification certificates, list of events classified, and supporting documents.",
  support_staff: "Please upload experience certificates, list of supported players, and supporting documents.",
  association: "Please upload district achievements, list of players, tournaments conducted, and supporting documents.",
  ngo: "Please upload list of players supported, details of support provided, and NGO registration documents.",
  educational_institution: "Please upload details of para sports facilities provided, list of para athlete students, and supporting documents.",
  individual_organization: "Please upload details of contributions to para sports, list of players supported, and supporting documents."
};

const ExcellenceAwards = ({ checkStatusDirect = false }) => {
  const [activeSettings, setActiveSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [isApplyingAllowed, setIsApplyingAllowed] = useState(true);
  const formRef = useRef(null);

  // Form State
  const [step, setStep] = useState(1);
  const [applicantType, setApplicantType] = useState("");
  const [awardCategory, setAwardCategory] = useState("");

  // Search state for registered players
  const [mobileSearch, setMobileSearch] = useState("");
  const [searchingPlayer, setSearchingPlayer] = useState(false);
  const [typeCounts, setTypeCounts] = useState({});
  const [categoryCounts, setCategoryCounts] = useState({});
  const [isRegisteredPlayer, setIsRegisteredPlayer] = useState(false);
  const [fetchedPlayerId, setFetchedPlayerId] = useState(null);
  const [existingAchievements, setExistingAchievements] = useState([]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    relation_name: "",
    dob: "",
    gender: "",
    experience_years: "",
    players_trained: "",
    contributions: "",
    address: "",
    district: "",
    state: "Andhra Pradesh",
    district_president_secretary_name: "",
    aadhaar_number: "",
  });

  const [achievements, setAchievements] = useState([]); // List of achievements submitted
  const [photo, setPhoto] = useState(null);
  const [mainDocument, setMainDocument] = useState(null);
  const [aadhaarDocument, setAadhaarDocument] = useState(null);
  const [dobProof, setDobProof] = useState(null);
  const [eventMedia, setEventMedia] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [aadhaarConsent, setAadhaarConsent] = useState(false);

  // Dynamic association districts
  const [associationDistricts, setAssociationDistricts] = useState([]);

  // Status check states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusMobile, setStatusMobile] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [statusForms, setStatusForms] = useState({});
  const [submittingAdditionalId, setSubmittingAdditionalId] = useState(null);
  const [submittingAdditional, setSubmittingAdditional] = useState(false);
  const [activeStatusTab, setActiveStatusTab] = useState(null);

  // Additional uploads states

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await API.get("/excellence-awards/active");
        setActiveSettings(res.settings);
        setTypeCounts(res.counts || {});
        setCategoryCounts(res.category_counts || {});
        setIsApplyingAllowed(res.is_applying_allowed !== false);
      } catch (err) {
        console.error("Failed to load active settings", err);
      } finally {
        setLoadingSettings(false);
      }
    };
    const fetchAssocDistricts = async () => {
      try {
        const res = await API.fetchExcellenceAssociationDistricts();
        setAssociationDistricts(res || []);
      } catch (err) {
        console.error("Failed to load association districts", err);
      }
    };
    fetchSettings();
    fetchAssocDistricts();
  }, []);

  useEffect(() => {
    // Detect mobile or scan hash to directly scroll to the application form
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    const hasHash = window.location.hash === "#apply-form" || window.location.search.includes("apply");
    
    if ((isMobile || hasHash) && !loadingSettings) {
      const timer = setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loadingSettings]);

  const handleSearchPlayer = async () => {
    if (!mobileSearch) {
      Swal.fire("Error", "Please enter a mobile number to search", "error");
      return;
    }
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(mobileSearch)) {
      Swal.fire("Error", "Please enter a valid 10-digit mobile number.", "error");
      return;
    }
    setSearchingPlayer(true);
    try {
      const res = await API.get(`/excellence-awards/search-player?mobile=${mobileSearch}`);
      if (res.found) {
        const selectedApplicantType = applicantType || "player";
        const isDistrictAssociation = selectedApplicantType === "association" || selectedApplicantType === "district_association";
        const userRecord   = res.user_record   || null;
        const commonPlayer = res.player        || {};

        if (isDistrictAssociation) {
          // District association:
          // Personal details: prefer players table (if registered), else users table
          // Logo: always from users table (user_record.logo_path)
          const playerSrc = res.player_record || null;
          const userSrc   = userRecord        || null;

          setForm((prev) => ({
            ...prev,
            // Personal info: players table first, then users table, then commonPlayer fallback
            name:           (playerSrc && playerSrc.name)     || (userSrc && userSrc.name)     || commonPlayer.name     || "",
            relation_name:  commonPlayer.relation || "",
            email:          commonPlayer.email    || "",
            mobile:         mobileSearch,
            dob:            commonPlayer.dob      || "",
            gender:         commonPlayer.gender   || "",
            address:        commonPlayer.address  || "",
            // District: prefer users table (authoritative for associations), then players, then common
            district:       (userSrc && userSrc.district)     || (playerSrc && playerSrc.district) || commonPlayer.district || "",
            state:          "Andhra Pradesh",
            aadhaar_number: commonPlayer.aadhaar_number || "",
            // Logo always from users table
            _district_logo: userSrc
              ? (userSrc.logo_path || userSrc.district_logo_path || null)
              : null,
          }));
          setFetchedPlayerId(commonPlayer.id || null);
          setExistingAchievements(res.achievements || []);
          setIsRegisteredPlayer(true);
          const districtLabel = (userSrc && userSrc.district) || (playerSrc && playerSrc.district) || commonPlayer.district || "Unknown";
          const sourceLabel   = playerSrc ? "players & users tables" : "users table";
          Swal.fire("Found!", `District association details for "${districtLabel}" loaded from ${sourceLabel}.`, "success");
        } else {

          // Para player: fill from players table
          setForm((prev) => ({
            ...prev,
            name:           commonPlayer.name           || "",
            relation_name:  commonPlayer.relation       || "",
            email:          commonPlayer.email          || "",
            mobile:         mobileSearch,
            dob:            commonPlayer.dob            || "",
            gender:         commonPlayer.gender         || "",
            address:        commonPlayer.address        || "",
            district:       commonPlayer.district       || (userRecord ? userRecord.district : "") || "",
            state:          "Andhra Pradesh",
            aadhaar_number: commonPlayer.aadhaar_number || "",
          }));
          setFetchedPlayerId(commonPlayer.id || null);
          setExistingAchievements(res.achievements || []);
          setIsRegisteredPlayer(true);
          Swal.fire("Found!", `${commonPlayer.name || "Applicant"} details loaded successfully.`, "success");
        }
      } else {
        setIsRegisteredPlayer(false);
        setFetchedPlayerId(null);
        setExistingAchievements([]);
        setForm((prev) => ({ ...prev, mobile: mobileSearch, address: "", district: "" }));
        Swal.fire({
          icon: "info",
          title: "Not Registered",
          text: "No record found with this mobile number. You can fill out details manually.",
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to search applicant", "error");
    } finally {
      setSearchingPlayer(false);
    }
  };


  const handleInputChange = (e) => {
    let val = e.target.value;
    if (e.target.name === "mobile") {
      val = normalizeMobile(val);
    }
    setForm({ ...form, [e.target.name]: val });
  };

  const handleAddAchievement = () => {
    setAchievements([
      ...achievements,
      { title: "", level: "", medal: "None", date: "", notes: "", file: null }
    ]);
  };

  const handleRemoveAchievement = (index) => {
    const list = [...achievements];
    list.splice(index, 1);
    setAchievements(list);
  };

  const handleAchievementChange = (index, field, value) => {
    const list = [...achievements];
    list[index][field] = value;
    setAchievements(list);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!applicantType || !awardCategory || !form.name || !form.mobile) {
      Swal.fire("Error", "Please fill in all mandatory fields", "error");
      return;
    }
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(form.mobile)) {
      Swal.fire("Error", "Please enter a valid 10-digit mobile number.", "error");
      return;
    }
    if (!form.aadhaar_number || form.aadhaar_number.length !== 12) {
      Swal.fire("Error", "Please enter a valid 12-digit Aadhaar Number.", "error");
      return;
    }
    if (!aadhaarConsent) {
      Swal.fire("Error", "Please accept the Aadhaar verification consent statement to proceed.", "error");
      return;
    }
    if (!photo) {
      Swal.fire("Error", "Applicant Photo is mandatory for all", "error");
      return;
    }
    if (!aadhaarDocument) {
      Swal.fire("Error", "Aadhaar Document upload is mandatory for all", "error");
      return;
    }
    if (applicantType === "player" && !dobProof) {
      Swal.fire("Error", "Date of Birth proof is mandatory for players", "error");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("year", activeSettings?.year || 2026);
      formData.append("applicant_type", applicantType);
      formData.append("award_category", awardCategory);
      formData.append("name", form.name);
      formData.append("mobile", form.mobile);
      formData.append("email", form.email || "");
      if (fetchedPlayerId) formData.append("player_id", fetchedPlayerId);
      formData.append("relation_name", form.relation_name || "");
      formData.append("dob", form.dob || "");
      formData.append("gender", form.gender || "");
      formData.append("experience_years", form.experience_years || "");
      formData.append("players_trained", form.players_trained || "");
      formData.append("contributions", form.contributions || "");
      formData.append("address", form.address || "");
      formData.append("district", form.district || "");
      formData.append("state", "Andhra Pradesh");
      formData.append("district_president_secretary_name", form.district_president_secretary_name || "");
      formData.append("aadhaar_number", form.aadhaar_number || "");

      if (photo) formData.append("photo", photo);
      if (mainDocument) formData.append("main_document", mainDocument);
      if (aadhaarDocument) formData.append("aadhaar_document", aadhaarDocument);
      if (dobProof) formData.append("dob_proof", dobProof);

      // Append multiple event media files
      eventMedia.forEach((file) => {
        formData.append("event_media[]", file);
      });

      // Serialize achievements structure
      const listToSerialize = achievements.map((ach, idx) => ({
        title: ach.title,
        level: ach.level,
        medal: ach.medal,
        date: ach.date,
        notes: ach.notes,
        status: 'pending'
      }));

      formData.append("achievements", JSON.stringify(listToSerialize));

      // Append files separately with numbered keys matching serialization index
      achievements.forEach((ach, idx) => {
        if (ach.file) {
          formData.append(`achievement_files_${idx}`, ach.file);
        }
      });

      const res = await API.post("/excellence-awards/apply", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Application Submitted!",
          text: "Your application for the Excellence Awards has been received.",
          confirmButtonText: "Return Home"
        }).then(() => {
          window.location.href = "/";
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Application submission failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const buildStatusForms = (applications) => {
    const nextForms = {};
    applications.forEach((app) => {
      nextForms[app.id] = {
        address: app.address || "",
        district: app.district || "",
        aadhaarNumber: app.aadhaar_number || "",
        photo: null,
        aadhaarDoc: null,
        dobProof: null,
        mainDoc: null,
        additionalFiles: []
      };
    });
    return nextForms;
  };

  const updateStatusForm = (applicationId, patch) => {
    setStatusForms((prev) => ({
      ...prev,
      [applicationId]: {
        ...(prev[applicationId] || {}),
        ...patch
      }
    }));
  };

  const handleFileAdd = (applicationId, files) => {
    const currentFiles = statusForms[applicationId]?.additionalFiles || [];
    const newFiles = Array.from(files);
    updateStatusForm(applicationId, {
      additionalFiles: [...currentFiles, ...newFiles]
    });
  };

  const handleFileRemove = (applicationId, idx) => {
    const currentFiles = statusForms[applicationId]?.additionalFiles || [];
    const updatedFiles = currentFiles.filter((_, i) => i !== idx);
    updateStatusForm(applicationId, {
      additionalFiles: updatedFiles
    });
  };

  const handleStatusCheck = async (overrideMobile = null) => {
    const mobileToUse = (typeof overrideMobile === "string") ? overrideMobile : statusMobile;
    const mobileRegex = /^\d{10}$/;
    if (!mobileToUse || !mobileRegex.test(mobileToUse)) {
      Swal.fire("Error", "Please enter a valid 10-digit mobile number.", "error");
      return;
    }
    setCheckingStatus(true);
    setStatusData(null);
    setStatusForms({});
    try {
      const res = await API.checkExcellenceAwardStatus(mobileToUse);
      if (res.found) {
        const applications = Array.isArray(res.applications)
          ? res.applications
          : (res.application ? [{ ...res.application, missing: res.missing || {} }] : []);
        setStatusData({ ...res, applications });
        setStatusForms(buildStatusForms(applications));
        if (applications.length > 0) {
          setActiveStatusTab(applications[0].id);
        }
      } else {
        Swal.fire("Not Found", res.message || "No application found for this mobile number.", "info");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to check application status", "error");
    } finally {
      setCheckingStatus(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasStatus = params.has("status") || window.location.pathname.endsWith("/status") || checkStatusDirect;
    const mobileParam = params.get("mobile") || "";

    if (hasStatus) {
      setShowStatusModal(true);
      const cleanMobile = normalizeMobile(mobileParam);
      if (cleanMobile && /^[6-9]\d{9}$/.test(cleanMobile)) {
        setStatusMobile(cleanMobile);
        handleStatusCheck(cleanMobile);
      }
    }
  }, [checkStatusDirect]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdditionalSubmit = async (e, applicationId) => {
    e.preventDefault();
    const formState = statusForms[applicationId] || {};
    setSubmittingAdditional(true);
    setSubmittingAdditionalId(applicationId);
    try {
      const formData = new FormData();
      formData.append("mobile", statusMobile);
      formData.append("application_id", applicationId);
      if (formState.address) formData.append("address", formState.address);
      if (formState.district) formData.append("district", formState.district);
      if (formState.aadhaarNumber) formData.append("aadhaar_number", formState.aadhaarNumber);
      if (formState.photo) formData.append("photo", formState.photo);
      if (formState.aadhaarDoc) formData.append("aadhaar_document", formState.aadhaarDoc);
      if (formState.dobProof) formData.append("dob_proof", formState.dobProof);
      if (formState.mainDoc) formData.append("main_document", formState.mainDoc);

      (formState.additionalFiles || []).forEach((file) => {
        formData.append("additional_docs[]", file);
      });

      const res = await API.uploadExcellenceAdditionalDocs(formData);
      if (res.success) {
        Swal.fire("Success!", "Additional data and files uploaded successfully.", "success");
        await handleStatusCheck();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to submit documents", "error");
    } finally {
      setSubmittingAdditional(false);
      setSubmittingAdditionalId(null);
    }
  };
  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 text-emerald-600">
        <FaMedal className="animate-spin text-4xl" />
      </div>
    );
  }

  const currentYear = activeSettings?.year || 2026;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans">
      <Navbar />

      {/* ===== HERO POSTER SECTION ===== */}
      <section className="relative py-10 md:py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 border-b border-emerald-500/20 text-center text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.08)_0%,_transparent_70%)]"></div>
        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          {/* Register Button - Mobile Only */}
          <div className="block sm:hidden mb-6 space-y-3">
            {isApplyingAllowed ? (
              <button
                type="button"
                onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="w-full px-6 py-3 bg-yellow-300 hover:bg-yellow-400 text-slate-950 font-black rounded-xl transition-all shadow-md active:scale-95 duration-150 text-sm inline-flex items-center justify-center gap-2"
              >
                <FaTrophy /> Register for Awards Here
              </button>
            ) : (
              <div className="w-full px-6 py-3 bg-red-500/25 border border-red-500/40 text-red-200 font-extrabold rounded-xl text-xs text-center">
                🔴 Applications Closed
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowStatusModal(true)}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-750 text-white font-bold rounded-xl transition-all shadow-md active:scale-95 duration-150 text-sm inline-flex items-center justify-center gap-2"
            >
              <FaSearch /> Check Status / Upload Docs
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 text-yellow-300 font-extrabold uppercase tracking-widest text-xs sm:text-sm bg-white/10 px-4 py-2 rounded-full border border-white/20 max-w-fit mx-auto mb-6">
            <FaTrophy className="animate-bounce" /> National Sports Day Awards
          </div>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-white uppercase leading-none">
            Para Sports <br className="sm:hidden" />
            <span className="text-yellow-300">
              Excellence Awards - {currentYear}
            </span>
          </h1>
          <p className="text-teal-100/90 italic font-medium tracking-wide mt-4 text-sm sm:text-lg">
            Recognising Excellence | Inspiring Inclusion | Empowering Champions
          </p>

          {/* Register Button - Desktop/Tablet Only */}
          <div className="hidden sm:flex justify-center gap-4 mt-8">
            {isApplyingAllowed ? (
              <button
                type="button"
                onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="px-6 py-3 bg-yellow-300 hover:bg-yellow-400 text-slate-950 font-black rounded-xl transition-all shadow-md transform hover:scale-105 active:scale-95 duration-150 text-sm inline-flex items-center gap-2"
              >
                <FaTrophy /> Register for Awards Here
              </button>
            ) : (
              <div className="px-6 py-3 bg-red-500/25 border border-red-500/40 text-red-200 font-extrabold rounded-xl text-xs flex items-center justify-center">
                🔴 Applications Closed
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowStatusModal(true)}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-md transform hover:scale-105 active:scale-95 duration-150 text-sm inline-flex items-center gap-2"
            >
              <FaSearch /> Check Status / Upload Docs
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10 flex flex-col items-center shadow-lg">
              <FaCalendarAlt className="text-yellow-300 text-3xl mb-3" />
              <h3 className="text-xs text-teal-150 font-bold uppercase tracking-wider">Submission Deadline</h3>
              <p className="text-lg font-bold text-white mt-1">
                {activeSettings ? new Date(activeSettings.deadline_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : "15th August 2026"}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10 flex flex-col items-center shadow-lg">
              <FaTrophy className="text-yellow-300 text-3xl mb-3" />
              <h3 className="text-xs text-teal-150 font-bold uppercase tracking-wider">Ceremony Date</h3>
              <p className="text-lg font-bold text-white mt-1">
                {activeSettings ? new Date(activeSettings.ceremony_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : "5th September 2026"}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10 flex flex-col items-center shadow-lg">
              <FaMapMarkerAlt className="text-yellow-300 text-3xl mb-3" />
              <h3 className="text-xs text-teal-150 font-bold uppercase tracking-wider">Awards Venue</h3>
              <p className="text-lg font-bold text-white mt-1 text-center">
                {activeSettings?.venue || "Visakhapatnam, Andhra Pradesh"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TELUGU ANNOUNCEMENT SECTION ===== */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-950 py-16 border-b border-emerald-950">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-950 via-teal-950 to-slate-950 rounded-3xl shadow-2xl border border-amber-500/30 overflow-hidden text-white transition-all duration-300 hover:border-amber-500/50">
            {/* Header Ribbon */}
            <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-600 text-slate-950 px-6 py-4 flex flex-wrap justify-between items-center gap-2 shadow-md">
              <span className="flex items-center gap-2 text-xs sm:text-sm font-extrabold tracking-wide uppercase">
                <FaTrophy className="text-slate-950 animate-pulse" /> రాష్ట్ర స్థాయి అవార్డుల ప్రకటన (State Level Awards Announcement)
              </span>
              <span className="bg-slate-950/20 text-slate-950 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider">
                తెలుగు సంస్కరణ
              </span>
            </div>

            <div className="p-6 sm:p-10 space-y-8">
              {/* Introduction Quote */}
              <div className="max-w-4xl mx-auto text-center space-y-4">
                <h2 className="text-2xl sm:text-3xl font-black text-amber-400 leading-snug drop-shadow-md">
                  పారా క్రీడల్లో ప్రతిభ, సేవ, అంకితభావానికి రాష్ట్ర స్థాయి గౌరవం! పారా స్పోర్ట్స్ ఎక్సెలెన్స్ అవార్డ్స్
                </h2>
                
                <div className="relative p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 max-w-3xl mx-auto my-6 italic text-teal-100 text-sm sm:text-base leading-relaxed shadow-lg">
                  <span className="absolute -top-3 left-4 text-4xl text-amber-400 font-serif leading-none">"</span>
                  గౌరవం ఒక అవార్డు మాత్రమే కాదు... అది ఒక క్రీడాకారుడు, కోచ్, టెక్నికల్ అధికారి, వాలంటీర్ మరియు పారా క్రీడల అభివృద్ధికి సేవలందించిన ప్రతి ఒక్కరి అంకితభావానికి సమాజం అందించే అత్యున్నత గుర్తింపు.
                  <span className="absolute -bottom-7 right-4 text-4xl text-amber-400 font-serif leading-none">"</span>
                </div>

                <p className="text-teal-100/90 text-sm sm:text-base leading-relaxed font-medium font-sans">
                  జాతీయ క్రీడా దినోత్సవం సందర్భంగా <span className="font-extrabold text-amber-300">Para Sports Association of Andhra Pradesh (PSAAP)</span> ఆధ్వర్యంలో <span className="font-bold text-white font-sans">National Sports Day – Para Sports Excellence Awards {currentYear}</span> రాష్ట్ర స్థాయి అవార్డుల కోసం దరఖాస్తులను ఆహ్వానిస్తున్నాము.
                </p>
              </div>

              {/* Main Content Grid: Who Can Apply & Critical Dates */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6 border-t border-white/10">
                {/* Left side: Who can apply */}
                <div className="bg-white/5 backdrop-blur-md p-6 rounded-2xl border border-white/10 space-y-4 flex flex-col justify-between shadow-lg">
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-amber-300 flex items-center gap-2 leading-relaxed">
                      యే వ్యక్తులు దరఖాస్తు చేసుకోవడానికి అర్హులు?
                    </h3>
                    <p className="text-xs text-teal-300 font-bold uppercase tracking-wider">
                      ఈ క్రింది వ్యక్తులు మరియు సంస్థలు దరఖాస్తు చేసుకోవచ్చు:
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-teal-50 font-medium leading-relaxed font-sans">
                      <div className="flex items-start gap-3 bg-white/5 hover:bg-emerald-950/40 p-3 rounded-xl border border-white/5 hover:border-amber-500/30 shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <FaMedal className="text-amber-400 text-lg drop-shadow-sm shrink-0" />
                        <span>పారా క్రీడాకారులు (సబ్-జూనియర్, జూనియర్, సీనియర్)</span>
                      </div>
                      <div className="flex items-start gap-3 bg-white/5 hover:bg-emerald-950/40 p-3 rounded-xl border border-white/5 hover:border-amber-500/30 shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <FaRunning className="text-amber-400 text-lg drop-shadow-sm shrink-0" />
                        <span>కోచ్లు (Coaches) & శిక్షకులు</span>
                      </div>
                      <div className="flex items-start gap-3 bg-white/5 hover:bg-emerald-950/40 p-3 rounded-xl border border-white/5 hover:border-amber-500/30 shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <FaBalanceScale className="text-amber-400 text-lg drop-shadow-sm shrink-0" />
                        <span>టెక్నికల్ అధికారులు, క్లాసిఫైయర్లు & రిఫరీలు</span>
                      </div>
                      <div className="flex items-start gap-3 bg-white/5 hover:bg-emerald-950/40 p-3 rounded-xl border border-white/5 hover:border-amber-500/30 shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <FaHandshake className="text-amber-400 text-lg drop-shadow-sm shrink-0" />
                        <span>స్వచ్ఛంద సేవకులు & సహాయక సిబ్బంది</span>
                      </div>
                      <div className="flex items-start gap-3 bg-white/5 hover:bg-emerald-950/40 p-3 rounded-xl border border-white/5 hover:border-amber-500/30 shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <FaBuilding className="text-amber-400 text-lg drop-shadow-sm shrink-0" />
                        <span>జిల్లా పారా స్పోర్ట్స్ అసోసియేషన్లు & సంఘాలు</span>
                      </div>
                      <div className="flex items-start gap-3 bg-white/5 hover:bg-emerald-950/40 p-3 rounded-xl border border-white/5 hover:border-amber-500/30 shadow-md transition-all duration-300 hover:-translate-y-0.5">
                        <FaGraduationCap className="text-amber-400 text-lg drop-shadow-sm shrink-0" />
                        <span>విద్యాసంస్థలు & క్రీడా సంస్థలు</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-500/10 text-amber-300 p-4 rounded-xl border border-amber-500/20 text-xs italic font-bold text-center mt-4 font-sans tracking-wide">
                    "విజేతలను గౌరవించడం అంటే భవిష్యత్ విజేతలను ప్రోత్సహించడం."
                  </div>
                </div>

                {/* Right side: Dates & Apply Info */}
                <div className="space-y-6 flex flex-col justify-between">
                  <div className="bg-white/5 backdrop-blur-md p-6 rounded-2xl border border-white/10 space-y-4 shadow-lg">
                    <h3 className="text-lg font-bold text-amber-300 flex items-center gap-2 leading-relaxed">
                      ముఖ్యమైన తేదీలు & వివరాలు
                    </h3>
                    
                    <div className="space-y-3 text-xs leading-relaxed font-sans">
                      <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5 hover:border-amber-500/20 transition-all duration-350 shadow-sm">
                        <div className="flex items-center gap-3">
                          <span className="p-2 bg-rose-950/50 text-rose-300 rounded-lg text-sm border border-rose-900/30"><FaCalendarAlt /></span>
                          <div>
                             <p className="text-xs text-amber-300 font-extrabold uppercase tracking-wide">దరఖాస్తుల చివరి తేదీ</p>
                            <p className="text-xs text-teal-200 font-medium">Last Date to Apply</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-rose-300 bg-rose-950/50 px-3 py-1 rounded-lg border border-rose-900/30">
                          {activeSettings ? new Date(activeSettings.deadline_date).toLocaleDateString('te-IN', { month: 'long', day: 'numeric', year: 'numeric' }) : "15 ఆగస్టు 2026"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5 hover:border-amber-500/20 transition-all duration-350 shadow-sm">
                        <div className="flex items-center gap-3">
                          <span className="p-2 bg-emerald-950/50 text-emerald-300 rounded-lg text-sm border border-emerald-900/30"><FaMedal /></span>
                          <div>
                             <p className="text-xs text-amber-300 font-extrabold uppercase tracking-wide">అవార్డుల ప్రదానోత్సవం</p>
                            <p className="text-xs text-teal-200 font-medium">Award Ceremony</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-emerald-300 bg-emerald-950/50 px-3 py-1 rounded-lg border border-emerald-900/30 font-sans">
                          {activeSettings ? new Date(activeSettings.ceremony_date).toLocaleDateString('te-IN', { month: 'long', day: 'numeric', year: 'numeric' }) : "10 సెప్టెంబర్ 2026"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5 hover:border-amber-500/20 transition-all duration-350 shadow-sm">
                        <div className="flex items-center gap-3">
                          <span className="p-2 bg-cyan-950/50 text-cyan-300 rounded-lg text-sm border border-cyan-900/30"><FaMapMarkerAlt /></span>
                          <div>
                             <p className="text-xs text-amber-300 font-extrabold uppercase tracking-wide">స్థలం / వేదిక</p>
                            <p className="text-xs text-teal-200 font-medium">Ceremony Venue</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-cyan-300 bg-cyan-950/50 px-3 py-1 rounded-lg border border-cyan-900/30">
                          {activeSettings?.venue || "విశాఖపట్నం, ఆంధ్రప్రదేశ్"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Submission methods */}
                  <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 text-slate-900 p-6 rounded-2xl space-y-2.5 shadow-2xl border border-amber-400/20 mt-4 font-sans font-bold">
                    <h4 className="text-xs font-black uppercase tracking-wider flex items-center gap-2 leading-relaxed text-slate-950">
                      మీ దరఖాస్తు చేయడం ఎలా? (How to Apply)
                    </h4>
                    <ul className="text-xs space-y-1.5 font-bold leading-loose text-slate-900 opacity-95">
                      <li>⬢ పోస్టర్లోని <strong>QR Code</strong> స్కాన్ చేసి దరఖాస్తు చేసుకోండి లేదా కింద దరఖాస్తు ఫారమ్ ని పూర్తి చేసి సమర్పించండి.</li>
                      <li>⬢ లేదా మీ దరఖాస్తును నేరుగా ఇమెయిల్ <strong>apparasports@gmail.com</strong> ద్వారా మాకు పంపవచ్చు.</li>
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-2 font-bold text-teal-200">
                    <a href="tel:9390131777" className="hover:text-amber-300 flex items-center gap-1 font-sans"><FaPhone /> 9390131777</a>
                    <a href="mailto:apparasports@gmail.com" className="hover:text-amber-300 flex items-center gap-1 font-sans"><FaEnvelope /> apparasports@gmail.com</a>
                  </div>
                </div>
              </div>

              {/* Telugu Sign-Off */}
              <div className="pt-6 border-t border-white/10 mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs leading-loose">
                <span className="text-teal-250 italic text-center sm:text-left font-sans opacity-85">
                  "ప్రతిభకు గుర్తింపు లభిస్తే మరింత మంది విజేతలు పుడతారు... సేవకు గౌరవం దక్కితే సమాజం మరింత ముందుకు సాగుతుంది."
                </span>
                <div className="bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl text-right shrink-0">
                  <p className="font-bold text-amber-300">వి. రామస్వామి</p>
                  <p className="text-teal-200 font-semibold text-[10px]">కార్యదర్శి, PSAAP</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
      
      {/* ===== INFO & APPLICATION PROCESS ===== */}
      <section className="py-16 max-w-6xl mx-auto px-4 flex flex-col lg:flex-row gap-12">

        {/* LEFT COLUMN: CATEGORIES & INFO */}
        <div className="w-full lg:w-1/3 space-y-8 order-2 lg:order-1">
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm text-left">
            <h2 className="text-xl font-extrabold text-slate-800 border-b border-slate-100 pb-3 mb-4">
              Award Categories
            </h2>
              {AWARD_CATEGORIES.map((cat) => {
                const count = categoryCounts[cat.name] || 0;
                return (
                  <div key={cat.id} className="text-xs bg-slate-50 p-2.5 rounded-xl border border-slate-150 hover:border-emerald-500/20 hover:bg-emerald-50/20 transition-all flex items-center justify-between gap-2">
                    <div className="flex items-start gap-2 text-left">
                      <span className="text-emerald-600 font-extrabold">{cat.id}.</span>
                      <span className="text-slate-700 font-medium">{cat.name}</span>
                    </div>
                    <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black shrink-0 text-[10px]">
                      {count}
                    </span>
                  </div>
                );
              })}
          </div>

          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm text-left">
            <h2 className="text-sm font-extrabold text-slate-800 border-b border-slate-100 pb-3 mb-4 uppercase tracking-wider">
              Application Statistics ({currentYear})
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-600 font-bold">Athlete Applications:</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black">{typeCounts.player || 0}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-600 font-bold">Coach Applications:</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black">{typeCounts.coach || 0}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-600 font-bold">Technical Officials:</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black">{typeCounts.technical_official || 0}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-600 font-bold">Classifiers:</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black">{typeCounts.classifier || 0}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-600 font-bold">Support Staff:</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black">{typeCounts.support_staff || 0}</span>
              </div>
              <div className="flex justify-between items-center text-xs border-t border-slate-100 pt-3">
                <span className="text-slate-600 font-bold">Associations & NGOs:</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded font-black">
                  {(typeCounts.association || 0) + (typeCounts.ngo || 0) + (typeCounts.educational_institution || 0) + (typeCounts.individual_organization || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50/50 to-teal-50/30 p-6 rounded-3xl border border-emerald-100 text-left">
            <h3 className="font-extrabold text-slate-800 text-sm uppercase tracking-wider">Need Assistance?</h3>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              For any questions regarding application files or categories, feel free to contact:
            </p>
            <div className="mt-4 space-y-1 text-xs">
              <p className="font-bold text-slate-800">{activeSettings?.contact_name || "V. Ramaswamy"}</p>
              <p className="text-slate-600">Secretary, PSAAP</p>
              <p className="text-emerald-700 font-extrabold">{activeSettings?.contact_phone || "9390131777"}</p>
              <p className="text-slate-500">{activeSettings?.contact_email || "apparasports@gmail.com"}</p>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: APPLICATION FORM */}
        <div ref={formRef} id="apply-form" className="w-full lg:w-2/3 bg-white p-8 rounded-3xl border border-slate-200 shadow-md text-left order-1 lg:order-2">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-8">
            <h2 className="text-2xl font-black text-slate-900">Awards Application Form</h2>
            <span className="text-xs text-slate-500 font-bold bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
              Step {step} of 3
            </span>
          </div>

          {!isApplyingAllowed ? (
            <div className="bg-red-50 border border-red-200 rounded-3xl p-8 text-center space-y-3">
              <FaExclamationTriangle size={48} className="mx-auto text-red-500 mb-2 animate-bounce" />
              <h3 className="font-extrabold text-red-800 text-lg">Applications are Closed</h3>
              <p className="text-xs text-red-650 mt-1 leading-relaxed">
                Applications for the Excellence Awards {currentYear} are currently closed. The deadline was {activeSettings ? new Date(activeSettings.deadline_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}. You can still check your application status using the lookup above.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* STEP 1: CATEGORY & APPLICANT SELECT */}
              {step === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">
                    Who is Applying? <span className="text-rose-600">*</span>
                  </label>
                  <select
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    value={applicantType}
                    onChange={(e) => {
                      setApplicantType(e.target.value);
                      setIsRegisteredPlayer(false);
                      setFetchedPlayerId(null);
                      setExistingAchievements([]);
                      setAwardCategory("");
                    }}
                    required
                  >
                    <option value="" className="text-slate-800 bg-white">Select applicant type...</option>
                    {APPLICANT_TYPES.map((type) => (
                      <option key={type.id} value={type.id} className="text-slate-800 bg-white">{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">
                    Award Category <span className="text-rose-600">*</span>
                  </label>
                  <select
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    value={awardCategory}
                    onChange={(e) => setAwardCategory(e.target.value)}
                    required
                  >
                    <option value="" className="text-slate-800 bg-white">Select award category...</option>
                    {AWARD_CATEGORIES.filter((cat) => {
                      if (!applicantType) return true;
                      const allowedIds = CATEGORIES_BY_TYPE[applicantType] || [];
                      return allowedIds.includes(cat.id);
                    }).map((cat) => (
                      <option key={cat.id} value={cat.name} className="text-slate-800 bg-white">
                        {cat.id}. {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold transition-all text-sm shadow-sm"
                    disabled={!applicantType || !awardCategory}
                    onClick={() => setStep(2)}
                  >
                    Continue to Details
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: PROFILE DETAILS */}
            {step === 2 && (
              <div className="space-y-6">

                {applicantType && (
                  <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded-xl text-xs space-y-1">
                    <p className="font-extrabold uppercase tracking-wide">Category Note:</p>
                    <p className="font-semibold">{CATEGORY_NOTES[applicantType]}</p>
                    <p className="font-bold mt-1 text-emerald-800"><FaExclamationTriangle className="inline mr-1" /> Note: District-attested documents will be more effective.</p>
                  </div>
                )}

                {/* Mobile search lookup */}
                {applicantType && (
                  <div className="bg-emerald-50/50 p-6 rounded-2xl border border-emerald-100 mb-4 text-left">
                    <label className="block text-xs uppercase font-bold tracking-wider text-emerald-800 mb-2">
                      Registered Profile Lookup (by Mobile)
                    </label>
                    <p className="text-xs text-slate-500 mb-4">
                      Enter your mobile number to check for existing registrations and auto-fill details.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter 10-digit mobile number"
                        className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={mobileSearch}
                        onChange={(e) => {
                          const val = normalizeMobile(e.target.value);
                          setMobileSearch(val);
                        }}
                      />
                      <button
                        type="button"
                        className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center gap-2 text-sm"
                        onClick={handleSearchPlayer}
                        disabled={searchingPlayer}
                      >
                        <FaSearch /> {searchingPlayer ? "Searching..." : "Search"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 font-bold mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                      value={form.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-500 font-bold mb-1.5">Mobile Number *</label>
                    <input
                      type="text"
                      name="mobile"
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                      value={form.mobile}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 font-bold mb-1.5">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                      value={form.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  {applicantType === "player" && (
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-1.5">Father / Guardian Name</label>
                      <input
                        type="text"
                        name="relation_name"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={form.relation_name}
                        onChange={handleInputChange}
                      />
                    </div>
                  )}
                </div>

                {applicantType === "player" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-1.5">Date of Birth</label>
                      <input
                        type="date"
                        name="dob"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={form.dob}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-1.5">Gender</label>
                      <select
                        name="gender"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={form.gender}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Coach details */}
                {applicantType === "coach" && (
                  <div>
                    <label className="block text-xs text-slate-500 font-bold mb-1.5">Players Trained (Achievements & Names)</label>
                    <textarea
                      name="players_trained"
                      rows="4"
                      placeholder="List achievements of players you have coached..."
                      className="w-full bg-white border border-slate-200 rounded-xl p-4 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                      value={form.players_trained}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                )}

                {/* Non-players / general contributions description */}
                {["coach", "technical_official", "classifier", "support_staff", "association", "ngo", "educational_institution", "individual_organization"].includes(applicantType) && (
                  <div className="grid grid-cols-1 gap-4">
                    {applicantType !== "coach" && (
                      <div>
                        <label className="block text-xs text-slate-500 font-bold mb-1.5">Years of Experience</label>
                        <input
                          type="number"
                          name="experience_years"
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                          value={form.experience_years}
                          onChange={handleInputChange}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-1.5">Contributions to Para Sports this Year</label>
                      <textarea
                        name="contributions"
                        rows="4"
                        placeholder="Detail your work, contributions, or achievements in para sports this year..."
                        className="w-full bg-white border border-slate-200 rounded-xl p-4 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={form.contributions}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                  </div>
                )}

                {/* Address, District, State, President/Secretary, Aadhaar Number */}
                <div className="grid grid-cols-1 gap-4 border-t pt-4 mt-4 text-left">
                  <div>
                    {isRegisteredPlayer ? (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-150 text-left mb-4">
                        <span className="block text-xs text-slate-500 font-bold mb-1">Registered Address & District</span>
                        <p className="text-sm font-semibold text-slate-800">{form.address || "No address on file"}</p>
                        <p className="text-xs font-semibold text-slate-500 mt-1">District: {form.district || "N/A"}</p>
                        <p className="text-xs font-semibold text-slate-500">State: Andhra Pradesh</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-4">
                        <div>
                          <label className="block text-xs text-slate-500 font-bold mb-1.5">Address *</label>
                          <textarea
                            name="address"
                            rows="3"
                            className="w-full bg-white border border-slate-200 rounded-xl p-3 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                            value={form.address}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter full address..."
                          />
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs text-slate-500 font-bold mb-1.5">District *</label>
                            <select
                              name="district"
                              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                              value={form.district}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select District</option>
                              {(applicantType === "association" ? associationDistricts : AP_DISTRICTS).map((d) => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 font-bold mb-1.5">State</label>
                            <input
                              type="text"
                              name="state"
                              className="w-full bg-slate-100 border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none text-sm"
                              value="Andhra Pradesh"
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-1.5">District President / Secretary Name *</label>
                      <input
                        type="text"
                        name="district_president_secretary_name"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={form.district_president_secretary_name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter President or Secretary name..."
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-1.5">
                        Applicant Aadhaar Number *
                      </label>
                      <input
                        type="text"
                        name="aadhaar_number"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                        value={form.aadhaar_number}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").substring(0, 12);
                          setForm({ ...form, aadhaar_number: val });
                        }}
                        required
                        placeholder="Enter 12-digit Aadhaar number..."
                      />
                      <div className="mt-2 text-left">
                        <label className="flex items-start gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="mt-1 rounded text-emerald-600 focus:ring-emerald-500"
                            checked={aadhaarConsent}
                            onChange={(e) => setAadhaarConsent(e.target.checked)}
                          />
                          <span className="text-[10px] text-slate-500 font-semibold leading-normal">
                            నేను నా స్వచ్ఛంద అంగీకారం ద్వారా నా ఆధార్ సమాచారాన్ని ధృవీకరణ పురోజనాల కోసం ఉపయోగించడానికి అనుమతిస్తున్నాను. (I hereby give my voluntary consent to verify and use my Aadhaar details for verification purposes.) *
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    className="px-6 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold transition-all text-sm shadow-sm"
                    onClick={() => {
                      if (!form.name || !form.mobile) {
                        Swal.fire("Error", "Please fill in all mandatory fields", "error");
                        return;
                      }
                      if (form.mobile.length !== 10) {
                        Swal.fire("Error", "Mobile number must be exactly 10 digits", "error");
                        return;
                      }
                      setStep(3);
                    }}
                  >
                    Add Achievements
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: ACHIEVEMENTS & UPLOADS */}
            {step === 3 && (
              <div className="space-y-6">

                {/* Display existing achievements if registered player */}
                {isRegisteredPlayer && existingAchievements.length > 0 && (
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-150">
                    <h4 className="text-emerald-700 font-bold text-xs uppercase mb-3 text-left">Registered Medal Records</h4>
                    <div className="space-y-2">
                      {existingAchievements.map((ach) => (
                        <div key={ach.id} className="text-xs bg-white p-3 rounded-xl flex justify-between items-center border border-slate-200">
                          <div className="text-left">
                            <span className="font-extrabold text-slate-800">{ach.event_name}</span>
                            <span className="text-slate-500 ml-2">({ach.event_level})</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded font-extrabold text-[10px] ${ach.medal === "Gold" ? "bg-amber-400/10 text-amber-600" :
                              ach.medal === "Silver" ? "bg-slate-300 text-slate-700" :
                                ach.medal === "Bronze" ? "bg-amber-600/10 text-amber-600" : "bg-slate-100 text-slate-500"
                            }`}>
                            {ach.medal}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photo & Main Document Uploads */}
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-150 space-y-6">
                  <h4 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider mb-2">Mandatory Documents</h4>
                  <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded-xl text-xs flex items-center gap-2">
                    <FaExclamationTriangle className="text-amber-600 text-lg flex-shrink-0" />
                    <span className="font-semibold">Note: All documents should be properly scanned and with good quality. If documents are not properly scanned, the application may get rejected.</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-2">Applicant Photo <span className="text-rose-600">*</span></label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPhoto(e.target.files[0])}
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                        required
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Submit a high resolution profile photo. (Max 5MB)</p>
                    </div>

                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-2">Applicant Aadhaar Card / Document <span className="text-rose-600">*</span></label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setAadhaarDocument(e.target.files[0])}
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                        required
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Upload a copy of applicant's Aadhaar card (PDF or image). (Max 10MB)</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 border-t pt-4">
                    <div>
                      <label className="block text-xs text-slate-500 font-bold mb-2">
                        Main Document ({getMainDocLabel(applicantType)}) <span className="text-rose-600">*</span>
                      </label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setMainDocument(e.target.files[0])}
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                        required
                      />
                      <p className="text-[10px] text-emerald-600 mt-1 font-semibold">{getMainDocDesc(applicantType)} (Max 10MB)</p>
                    </div>

                    {applicantType === "player" && (
                      <div>
                        <label className="block text-xs text-slate-500 font-bold mb-2">DOB Proof (Age Proof) <span className="text-rose-600">*</span></label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) => setDobProof(e.target.files[0])}
                          className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                          required
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Birth certificate or official document for age validation. (Max 10MB)</p>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <label className="block text-xs text-slate-500 font-bold mb-2">Event Photos & Videos (Optional)</label>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={(e) => setEventMedia(Array.from(e.target.files))}
                      className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">You may select multiple photos or videos from corresponding sports events. (Max 20MB per file)</p>
                  </div>
                </div>

                {/* Multiple achievements upload */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-extrabold text-sm text-emerald-700 uppercase">Achievements & Proofs</h3>
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl border border-emerald-250 flex items-center gap-1 text-xs font-bold"
                      onClick={handleAddAchievement}
                    >
                      <FaPlus /> Add Record
                    </button>
                  </div>

                  {achievements.length === 0 ? (
                    <p className="text-xs text-slate-500 italic text-center py-6 border border-dashed border-slate-200 rounded-2xl">
                      No achievements added yet. Click 'Add Record' to submit achievements with supporting files.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {achievements.map((ach, index) => (
                        <div key={index} className="bg-slate-50 p-4 rounded-2xl border border-slate-200 relative">
                          <button
                            type="button"
                            className="absolute top-4 right-4 text-slate-400 hover:text-rose-600 transition-colors"
                            onClick={() => handleRemoveAchievement(index)}
                          >
                            <FaTrash />
                          </button>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-[10px] text-slate-500 font-bold mb-1">Achievement / Event Name *</label>
                              <input
                                type="text"
                                placeholder="E.g., Gold in Shotput State Meet"
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                value={ach.title}
                                onChange={(e) => handleAchievementChange(index, "title", e.target.value)}
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-[10px] text-slate-500 font-bold mb-1">Supporting Document (PDF/Image) *</label>
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                onChange={(e) => handleAchievementChange(index, "file", e.target.files[0])}
                                required
                              />
                              <p className="text-[9px] text-slate-400 mt-1">Upload achievement certificate or proof. (Max 10MB)</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div>
                              <label className="block text-[10px] text-slate-500 font-bold mb-1">Level</label>
                              <select
                                className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                value={ach.level}
                                onChange={(e) => handleAchievementChange(index, "level", e.target.value)}
                                required
                              >
                                <option value="" className="text-slate-800 bg-white">Select Level</option>
                                <option value="District" className="text-slate-800 bg-white">District</option>
                                <option value="State" className="text-slate-800 bg-white">State</option>
                                <option value="National" className="text-slate-800 bg-white">National</option>
                                <option value="International" className="text-slate-800 bg-white">International</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-slate-500 font-bold mb-1">Medal</label>
                              <select
                                className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                value={ach.medal}
                                onChange={(e) => handleAchievementChange(index, "medal", e.target.value)}
                              >
                                <option value="None">None</option>
                                <option value="Gold">Gold</option>
                                <option value="Silver">Silver</option>
                                <option value="Bronze">Bronze</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-slate-500 font-bold mb-1">Date</label>
                              <input
                                type="date"
                                className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                value={ach.date}
                                onChange={(e) => handleAchievementChange(index, "date", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-between pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    className="px-6 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
                    onClick={() => setStep(2)}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-extrabold shadow-md hover:from-emerald-700 hover:to-teal-700 transform hover:-translate-y-0.5 transition-all text-sm flex items-center gap-2"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting Application..." : <>Submit Application <FaTrophy /></>}
                  </button>
                </div>
              </div>
            )}
            </form>
          )}
        </div>
      </section>

      {/* ===== STATUS CHECK MODAL ===== */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex justify-between items-center text-white">
              <h3 className="font-black tracking-tight text-lg">Check Status & Upload Documents</h3>
              <button
                type="button"
                className="text-white/80 hover:text-white font-extrabold text-xl"
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusData(null);
                  setStatusMobile("");
                  setActiveStatusTab(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 text-left">
              {/* Lookup Form */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Enter applicant 10-digit mobile number"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-sm"
                    value={statusMobile}
                    onChange={(e) => {
                      setStatusMobile(normalizeMobile(e.target.value));
                      setStatusData(null);
                      setActiveStatusTab(null);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm flex items-center gap-2"
                  onClick={handleStatusCheck}
                  disabled={checkingStatus}
                >
                  {checkingStatus ? "Searching..." : "Check Status"}
                </button>
              </div>

              {statusData && (
                <div className="space-y-5 border-t pt-6">
                  {statusData.applications.length > 1 && (
                    <div className="flex gap-2 border-b pb-3 overflow-x-auto">
                      {statusData.applications.map((app) => (
                        <button
                          key={app.id}
                          type="button"
                          className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap ${
                            activeStatusTab === app.id
                              ? 'bg-emerald-600 text-white shadow-md border border-emerald-700'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-250 border border-slate-200'
                          }`}
                          onClick={() => setActiveStatusTab(app.id)}
                        >
                          {app.applicant_type === 'player' ? 'Para Player' : 
                           app.applicant_type === 'coach' ? 'Coach' :
                           app.applicant_type === 'district_association' ? 'District Association' : 
                           app.applicant_type} ({app.year})
                        </button>
                      ))}
                    </div>
                  )}

                  {(statusData.applications || [])
                    .filter((app) => statusData.applications.length <= 1 || activeStatusTab === app.id)
                    .map((app) => {
                      const missing = app.missing || {};
                      const formState = statusForms[app.id] || {};
                      const isSubmittingThis = submittingAdditionalId === app.id;
                    const hasRequest = app.requested_documents_desc || app.additional_documents_status === 'requested';

                    return (
                      <div key={app.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-slate-50 p-4 border-b border-slate-200">
                          <div>
                            <h4 className="font-extrabold text-slate-800">{app.name}</h4>
                            <p className="text-xs text-slate-500">{app.award_category} ({app.year})</p>
                            <p className="text-[10px] text-slate-400 uppercase font-bold mt-1">{APPLICANT_TYPES.find((type) => type.id === app.applicant_type)?.label || app.applicant_type}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full font-black text-xs uppercase self-start ${
                            app.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                            app.status === 'rejected' ? 'bg-rose-100 text-rose-800 border border-rose-200' :
                            'bg-amber-100 text-amber-800 border border-amber-200'
                          }`}>
                            {app.status}
                          </span>
                        </div>

                        <div className="p-4 space-y-4">
                          {app.status === 'approved' ? (
                            <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 p-5 rounded-2xl text-center space-y-2 border-dashed">
                              <p className="font-black text-xl text-emerald-800">Congratulations!</p>
                              <p className="text-sm font-semibold">Your application has been approved and accepted.</p>
                              <p className="text-xs text-slate-500 font-medium">Thank you for your outstanding dedication and contribution to Para Sports.</p>
                            </div>
                          ) : (
                            <>
                              {hasRequest && (
                                <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded-xl text-xs space-y-1">
                                  <p className="font-black text-amber-800 uppercase tracking-wide flex items-center gap-1"><FaExclamationTriangle className="text-amber-600" /> Admin Document Request:</p>
                                  <div 
                                    className="font-semibold text-slate-700 mt-1 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: app.requested_documents_desc || 'Please upload the requested supporting documents.' }}
                                  />
                                </div>
                              )}

                              <form onSubmit={(e) => handleAdditionalSubmit(e, app.id)} className="space-y-4">
                                <h4 className="font-bold text-xs uppercase text-emerald-700 tracking-wider">Provide Missing / Requested Information</h4>
                                <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded-xl text-xs flex items-center gap-2">
                                  <FaExclamationTriangle className="text-amber-600 text-lg flex-shrink-0" />
                                  <span className="font-semibold">Note: All documents should be properly scanned and with good quality. If documents are not properly scanned, the application may get rejected.</span>
                                </div>

                                {missing.address && (
                                  <div>
                                    <label className="block text-xs text-slate-500 font-bold mb-1">Full Address *</label>
                                    <textarea
                                      rows="2"
                                      className="w-full bg-white border border-slate-200 rounded-xl p-3 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                      value={formState.address || ""}
                                      onChange={(e) => updateStatusForm(app.id, { address: e.target.value })}
                                      required
                                      placeholder="Enter address..."
                                    />
                                  </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  {missing.district && (
                                    <div>
                                      <label className="block text-xs text-slate-500 font-bold mb-1">District *</label>
                                      <select
                                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                        value={formState.district || ""}
                                        onChange={(e) => updateStatusForm(app.id, { district: e.target.value })}
                                        required
                                      >
                                        <option value="">Select District</option>
                                        {AP_DISTRICTS.map((d) => (
                                          <option key={d} value={d}>{d}</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {missing.aadhaar_number && (
                                    <div>
                                      <label className="block text-xs text-slate-500 font-bold mb-1">Aadhaar Number *</label>
                                      <input
                                        type="text"
                                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                        value={formState.aadhaarNumber || ""}
                                        onChange={(e) => updateStatusForm(app.id, { aadhaarNumber: e.target.value.replace(/\D/g, "").substring(0, 12) })}
                                        required
                                        placeholder="Enter 12-digit Aadhaar..."
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  {missing.photo && (
                                    <div className="space-y-1">
                                      <label className="block text-xs text-slate-500 font-bold mb-1">Applicant Photo *</label>
                                      {app.photo_path && (
                                        <div className="bg-slate-50 border p-2.5 rounded-xl text-xs flex items-center gap-3">
                                          <div className="w-10 h-10 rounded-lg overflow-hidden border bg-slate-100 flex-shrink-0">
                                            <img
                                              src={resolveAssetUrl(app.photo_path)}
                                              alt="Previous Upload"
                                              className="w-full h-full object-cover"
                                            />
                                          </div>
                                          <div>
                                            <span className="block text-[9px] uppercase font-bold text-slate-400">Previously Uploaded Photo</span>
                                            <a
                                              href={resolveAssetUrl(app.photo_path)}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-xs text-emerald-600 hover:text-emerald-700 font-bold underline"
                                            >
                                              View Original
                                            </a>
                                          </div>
                                        </div>
                                      )}
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => updateStatusForm(app.id, { photo: e.target.files[0] })}
                                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                        required={!app.photo_path}
                                      />
                                      <p className="text-[10px] text-slate-400 mt-1">Submit a high resolution profile photo. (Max 5MB)</p>
                                    </div>
                                  )}

                                  {missing.aadhaar_document && (
                                    <div className="space-y-1">
                                      <label className="block text-xs text-slate-500 font-bold mb-1">Aadhaar Document *</label>
                                      {app.aadhaar_document_path && (
                                        <div className="bg-slate-50 border p-2.5 rounded-xl text-xs flex items-center gap-2">
                                          <span className="text-slate-400">📄</span>
                                          <div>
                                            <span className="block text-[9px] uppercase font-bold text-slate-400">Previously Uploaded Aadhaar</span>
                                            <a
                                              href={resolveAssetUrl(app.aadhaar_document_path)}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-xs text-emerald-600 hover:text-emerald-700 font-bold underline"
                                            >
                                              View Document
                                            </a>
                                          </div>
                                        </div>
                                      )}
                                      <input
                                        type="file"
                                        accept=".pdf,image/*"
                                        onChange={(e) => updateStatusForm(app.id, { aadhaarDoc: e.target.files[0] })}
                                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                        required={!app.aadhaar_document_path}
                                      />
                                      <p className="text-[10px] text-slate-400 mt-1">Upload a copy of applicant's Aadhaar card (PDF or image). (Max 10MB)</p>
                                    </div>
                                  )}

                                  {missing.dob_proof && (
                                    <div className="space-y-1">
                                      <label className="block text-xs text-slate-500 font-bold mb-1">DOB Proof *</label>
                                      {app.dob_proof_path && (
                                        <div className="bg-slate-50 border p-2.5 rounded-xl text-xs flex items-center gap-2">
                                          <span className="text-slate-400">📄</span>
                                          <div>
                                            <span className="block text-[9px] uppercase font-bold text-slate-400">Previously Uploaded DOB Proof</span>
                                            <a
                                              href={resolveAssetUrl(app.dob_proof_path)}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-xs text-emerald-600 hover:text-emerald-700 font-bold underline"
                                            >
                                              View Document
                                            </a>
                                          </div>
                                        </div>
                                      )}
                                      <input
                                        type="file"
                                        accept=".pdf,image/*"
                                        onChange={(e) => updateStatusForm(app.id, { dobProof: e.target.files[0] })}
                                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                        required={!app.dob_proof_path}
                                      />
                                      <p className="text-[10px] text-slate-400 mt-1">Birth certificate or official document for age validation. (Max 10MB)</p>
                                    </div>
                                  )}

                                  {missing.main_document && (
                                    <div className="space-y-1">
                                      <label className="block text-xs text-slate-500 font-bold mb-1">
                                        Main Document ({getMainDocLabel(app.applicant_type)}) *
                                      </label>
                                      {app.achievements?.main_document && (
                                        <div className="bg-slate-50 border p-2.5 rounded-xl text-xs flex items-center gap-2">
                                          <span className="text-slate-400">📄</span>
                                          <div>
                                            <span className="block text-[9px] uppercase font-bold text-slate-400">Previously Uploaded Main Document</span>
                                            <a
                                              href={resolveAssetUrl(app.achievements.main_document)}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-xs text-emerald-600 hover:text-emerald-700 font-bold underline"
                                            >
                                              View Document
                                            </a>
                                          </div>
                                        </div>
                                      )}
                                      <input
                                        type="file"
                                        accept=".pdf,image/*"
                                        onChange={(e) => updateStatusForm(app.id, { mainDoc: e.target.files[0] })}
                                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                        required
                                      />
                                      <p className="text-[10px] text-emerald-600 mt-1 font-semibold">{getMainDocDesc(app.applicant_type)} (Max 10MB)</p>
                                    </div>
                                  )}
                                </div>

                                <div className="border-t pt-4 space-y-3">
                                  <label className="block text-xs text-slate-700 font-extrabold mb-1">
                                    Upload Supporting Documents {missing.additional_docs && <span className="text-rose-600">*</span>}
                                  </label>

                                  {/* List of currently selected files to upload */}
                                  {formState.additionalFiles && formState.additionalFiles.length > 0 && (
                                    <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                                      <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1">Files to be Uploaded:</span>
                                      {formState.additionalFiles.map((file, idx) => (
                                        <div key={idx} className="flex justify-between items-center bg-white p-2 rounded-lg border text-xs">
                                          <span className="truncate font-semibold text-slate-755 max-w-[250px]">{file.name}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleFileRemove(app.id, idx)}
                                            className="text-rose-600 hover:text-rose-700 p-1 font-bold text-sm"
                                            title="Remove file"
                                          >
                                            <FaTrash />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* File Input styled nicely */}
                                  <div className="flex flex-col gap-2">
                                    <input
                                      type="file"
                                      accept=".pdf,image/*"
                                      multiple
                                      onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                          handleFileAdd(app.id, e.target.files);
                                          // Reset the input value so the same file can be selected again if removed
                                          e.target.value = "";
                                        }
                                      }}
                                      className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-500 text-xs"
                                      required={missing.additional_docs && (!formState.additionalFiles || formState.additionalFiles.length === 0)}
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1 font-medium font-sans">
                                      {missing.additional_docs 
                                        ? "Select one or more files as requested by the admin. You can select multiple files or add them one by one. (Max 10MB per file)" 
                                        : "Optional: Upload any other supporting documents (certificates, event proofs, etc.) if you wish. (Max 10MB per file)"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                  <button
                                    type="submit"
                                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-xs uppercase tracking-wide flex items-center gap-2 shadow-md"
                                    disabled={submittingAdditional}
                                  >
                                    {isSubmittingThis ? "Submitting..." : "Upload & Save Details"}
                                  </button>
                                </div>
                              </form>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ExcellenceAwards;
